import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CartTotalDisplay from "@insite/content-library/Components/CartTotalDisplay";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import { cartTotalStyles } from "@insite/content-library/Widgets/Cart/CartTotal";
import { DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow } from "@insite/mobius/index";
import Typography from "@insite/mobius/Typography";
import useLoadProductsWithAttributes from "@thkStore/Common/Hooks/useLoadProductsWithAttributes";
import getDaysToShipAttribute from "@thkStore/Common/Utils/Product/ProductGetDaysToShipAttribute";
import { CUSTOM_DAYS_TO_SHIP, CUSTOM_G1, CUSTOM_G2, CUSTOM_LENGTH, CUSTOM_PART_NUMBER, IS_CUSTOM_PRODUCT } from "@thkStore/Constants/ProductPropertiesKeys";
import { PrintInfo, PrintStyles } from "@thkStore/Styles/PrintStyled";
import * as React from "react";
import { connect } from "react-redux";
import { useSelector } from "react-redux";
import styled from "styled-components";

const enum fields {
    policyContent = "policyContent",
}

export const CartPrint = styled.div`
    line-height: 1;
    padding: 30px 20px 0;

    h1{
        color: ${({ theme }) => theme.colors.primary.main};
        font-size: 33px !important;
        font-weight: bold;
        text-align: center;
        margin-bottom: 20px;
    }

    .main-info {
        margin: 0;

        tbody {

            tr {

                &:nth-child(even) {
                    background-color: inherit;
                }

                td {
                    border-bottom: none;
                    height: inherit;
                    padding: 0;

                    &:first-child {
                        font-weight: bold;
                        text-align: right;
                    }

                    &:last-child {
                        width: 100%;
                        padding-left: 10px;
                    }
                }
            }
        }
    }

    u{
        font-size: 14px;
        font-weight: bold;
        display: block;
        margin: 25px 0 15px;
    }

    .products-table{
        margin: 20px 0;

        thead {

            tr {
                border: 1px solid ${({ theme }) => theme.colors.common.border};

                th {
                    background-color: #EDEBEC;
                    border-bottom: none;
                    height: 38px;

                    &[data-label="Cart Pdf Product Line"] {
                        width: 15%;
                    }

                    &[data-label="Cart Pdf Product Qty"] {
                        width: 7%;
                    }

                    span {
                        font-weight: normal;
                        display: block;
                        margin: 10px 0 5px;
                        white-space: normal;
                    }

                    @media print {
                        background-color: #EDEBEC;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        color-adjust: exact;
                    }
                }
            }
        }

        tbody {

            tr {

                &:nth-child(even) {
                    background-color: inherit;
                }
            }
        }
        
        span {
            font-size: 12px;
        }
    }
`

export const CartPrintData = styled.div`
    font-size: 12px;

    &.additional-text{
        display: flex;
        margin-top: 15px;

        div{
            flex: 1;
        }

        p{
            text-style: italic;

            &.additional-line{
                margin-top: 10px;
            }
        }
    }

    &.totals{
        font-weight: bold;
        text-align: right;

        [data-test-selector="cartTotal_current"] {
            background-color: inherit;
            padding: 0;

            [class^="GridOffset-sc"] {
                width: auto;
                margin: 0;
            
                [class^="GridItemStyle-sc-"] {
                    width: auto;
                    max-width: none;
                    flex-basis: auto;

                    &:nth-child(odd) {
                        width: calc(100% - 95px);
                    }

                    &:nth-child(even) {
                        width: 95px;
                    }
                }
            }
        }

        b{
            margin-right: 50px;
        }
    }

    b{
        font-weight: bold;
        display: inline-block;
        width: 125px;
        text-align: right;
        margin-right: 10px;
    }
`

const styles = cartTotalStyles;

// Widget Props Interface
interface OwnProps extends WidgetProps {
    fields: {
        [fields.policyContent]: string;
    };
}

// Map Redux State
const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const { productSettings } = getSettingsCollection(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;

    return {
        cart,
        isLoading: isCartLoading,
        billTo: cart?.billToId ? getBillToState(state, cart.billToId).value : undefined,
        shipTo: cart?.shipToId ? getShipToState(state, cart.shipToId).value : undefined,
        productSettings,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

// Widget Component
const CartPrintWidget: React.FC<Props> = ({ cart, billTo, shipTo, isLoading, productSettings, fields }) => {
    const productIds = cart?.cartLines?.map(line => line.productId) ?? [];
    useLoadProductsWithAttributes(productIds);

    const productsMap = useSelector((state: ApplicationState) => {
        const map: Record<string, ReturnType<typeof getProductState>["value"]> = {};
        productIds.forEach(id => {
            if (id) { map[id] = getProductState(state, id).value; }
        });
        return map;
    });

    if (isLoading || !cart || !cart.cartLines || cart.cartLines.length === 0) {
        return null;
    }

    const getDaysToShip = (cartLine: CartLineModel): string | undefined => {
        if (!cartLine.productId) {
            return undefined;
        }
        return cartLine.properties?.[CUSTOM_DAYS_TO_SHIP] || getDaysToShipAttribute(productsMap[cartLine.productId]);
    };

    const getProductDescription = (cartLine: CartLineModel): JSX.Element => {
        const isCustomProduct = cartLine?.properties?.[IS_CUSTOM_PRODUCT] && JSON.parse(cartLine.properties[IS_CUSTOM_PRODUCT].toLowerCase()) === true;
        if (!isCustomProduct) {        
            return <>{cartLine.shortDescription}</>;
        }

        const customProductInfo = isCustomProduct ? {
            length: cartLine.properties?.[CUSTOM_LENGTH],
            g1: cartLine.properties?.[CUSTOM_G1],
            g2: cartLine.properties?.[CUSTOM_G2],
        } : {};

        return <>
                    {cartLine.properties[CUSTOM_PART_NUMBER]}
                    <ul>
                        <li><SmallHeadingAndText heading="Length" text={customProductInfo?.length ?? ""} /></li>
                        <li><SmallHeadingAndText heading="G1" text={customProductInfo?.g1 ?? ""} /></li>
                        <li><SmallHeadingAndText heading="G2" text={customProductInfo?.g2 ?? ""} /></li>
                    </ul>
                </>;
    }

    return (
        <>
            <PrintStyles />
            <PrintInfo>
                <CartPrint>
                    <Typography as="h1">{translate("Cart Pdf Title")}<br />{translate("Cart Pdf Subtitle")}</Typography>

                    {/* Main Info */}
                    <DataTable className="main-info">
                        <DataTableBody>
                            <DataTableRow>
                                <DataTableCell><Typography as="b">{translate("Cart Pdf Date")}</Typography>:</DataTableCell>
                                <DataTableCell><Typography>{new Date().toLocaleDateString()}</Typography></DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell><Typography as="b">{translate("Cart Pdf Attn")}</Typography>:</DataTableCell>
                                <DataTableCell><Typography>{ shipTo?.firstName && shipTo?.lastName ? `${shipTo?.firstName} ${shipTo?.lastName}` : shipTo?.customerName }</Typography></DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell><Typography as="b">{translate("Cart Pdf Company name")}</Typography>:</DataTableCell>
                                <DataTableCell><Typography>{billTo?.companyName}</Typography></DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell><Typography as="u">{translate("Cart Pdf ShipTo")}</Typography></DataTableCell>
                                <DataTableCell></DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell><Typography as="b">{translate("Cart Pdf ShipTo Country")}</Typography>:</DataTableCell>
                                <DataTableCell><Typography>{shipTo?.country?.name}</Typography></DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell><Typography as="b">{translate("Cart Pdf ShipTo State")}</Typography>:</DataTableCell>
                                <DataTableCell><Typography>{shipTo?.state?.name}</Typography></DataTableCell>
                            </DataTableRow>
                            <DataTableRow>
                                <DataTableCell><Typography as="b">{translate("Cart Pdf ShipTo Zip")}</Typography>:</DataTableCell>
                                <DataTableCell><Typography>{shipTo?.postalCode}</Typography></DataTableCell>
                            </DataTableRow>
                        </DataTableBody>
                    </DataTable>

                    {/* Products Table */}
                    <DataTable className="products-table">
                        <DataTableHead>
                            <DataTableHeader data-label="Cart Pdf Product Line">{translate("Cart Pdf Product Line")}</DataTableHeader>
                            <DataTableHeader data-label="Cart Pdf Product Item">{translate("Cart Pdf Product Item")}</DataTableHeader>
                            <DataTableHeader data-label="Cart Pdf Product Part Number">{translate("Cart Pdf Product Part Number")}</DataTableHeader>
                            <DataTableHeader data-label="Cart Pdf Product Days to Ship">{translate("Cart Pdf Product Days to Ship")}</DataTableHeader>
                            <DataTableHeader data-label="Cart Pdf Unit Product Price">{translate("Cart Pdf Unit Product Price")}</DataTableHeader>
                            <DataTableHeader data-label="Cart Pdf Product Qty">{translate("Cart Pdf Product Qty")}</DataTableHeader>
                            <DataTableHeader data-label="Cart Pdf Product Price">{translate("Cart Pdf Product Price")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {cart?.cartLines?.map((cartLine) => {
                                const daysToShip = getDaysToShip(cartLine);
                                const productDescription = getProductDescription(cartLine);

                                return (
                                <DataTableRow key={cartLine.id}>
                                    <DataTableCell>{cartLine.line}</DataTableCell>
                                    <DataTableCell>{productDescription}</DataTableCell>
                                    <DataTableCell>{cartLine.erpNumber}</DataTableCell>
                                    <DataTableCell>{daysToShip || "-"}</DataTableCell>
                                    <DataTableCell>
                                        <ProductPrice
                                            product={cartLine}
                                            currencySymbol={cart.currencySymbol}
                                            showSavings={true}
                                            showSavingsAmount={productSettings.showSavingsAmount}
                                            showSavingsPercent={productSettings.showSavingsPercent}
                                            showInvalidPriceMessage={true}
                                            showLabel={false}
                                            showUnitOfMeasure={false}
                                            showPack={true}
                                        />
                                    </DataTableCell>
                                    <DataTableCell>{cartLine.qtyOrdered}</DataTableCell>
                                    <DataTableCell>
                                        <Typography>
                                            {cartLine.pricing?.extendedActualPriceDisplay}
                                        </Typography>
                                    </DataTableCell>
                                </DataTableRow>
                                );
                            })}
                        </DataTableBody>
                    </DataTable>

                    {/* Totals */}
                    <CartPrintData className="totals">
                        {cart && (
                            <CartTotalDisplay
                                showTaxAndShipping={true}
                                isLoading={false}
                                extendedStyles={styles.cartTotal}
                                isCartEmpty={false}
                                cart={cart}
                            />
                        )}
                    </CartPrintData>

                    {/* Additional Texts */}
                    <CartPrintData className="additional-text">
                        <Typography as="b">{translate("Cart Pdf Terms Title")}</Typography>
                        <Typography>{translate("Cart Pdf Terms Content")}</Typography>
                    </CartPrintData>
                    <CartPrintData className="additional-text">
                        <Typography as="b">{translate("Cart Pdf Validity Title")}</Typography>
                        <Typography>{translate("Cart Pdf Validity")}</Typography>
                    </CartPrintData>
                    <CartPrintData className="additional-text">
                        <Typography as="b">{translate("Cart Pdf Return Policy Title")}</Typography>
                        <StyledWrapper dangerouslySetInnerHTML={{ __html: fields.policyContent }} />
                    </CartPrintData>

                </CartPrint>
            </PrintInfo>
        </>
    );
};

// Widget Module Definition
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CartPrintWidget),
    definition: {
        group: "Cart",
        displayName: "Cart Print",
        icon: "print",
        allowedContexts: ["CartPage"],
        fieldDefinitions: [
            {
                name: fields.policyContent,
                displayName: "Policy Content",
                editorTemplate: "RichTextField",  // This allows multiple lines
                defaultValue: "",
                fieldType: "General",
                tooltip: "Enter each policy line on a new line",
                isRequired: true,
            }
        ],
    },
};

export default widgetModule;